import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import {
  SessionRecorder,
  TouchTracker,
  detectImplausibleSpeed,
  detectMultiTouch,
} from "@talent/client-telemetry";
import { wrongTapBudget, type DifferenceStimulus, DEMO_FORM } from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  glow,
  nudge,
  pop,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { drawToken } from "./token.js";
import { COLOR, familyColor, mix } from "./paint.js";

/**
 * Spot the difference — two panels, a few cells changed.
 *
 * **Both panels are live.** A found difference rings in both at once, and a tap counts wherever it
 * lands. Making one panel the "reference" and the other the "answer" would add a rule the child has
 * to be told, and there is no way to tell them without text (v2 §15).
 *
 * **Panels stack vertically.** Side by side on a phone the cells come out around 56 px, under the
 * ~76 px a child's aim needs; stacked at 3×2 they are roughly double that. The generator fixes the
 * grid at 3×2 for the same reason, and grows difficulty through the subtlety of the changes.
 *
 * The wrong taps are the interesting record here — the difference between scanning and guessing —
 * and they are carried as `corrections` on the trial rather than turned into anything about the
 * child.
 */

export interface DifferenceTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateStimulus: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => DifferenceStimulus;
  scoreTrial: (
    stimulus: DifferenceStimulus,
    found: readonly number[],
    wrongTaps: number,
  ) => boolean;
  /** Play the wordless demonstration first. Set on a child's first time with this game. */
  showTutorial?: boolean;
  /**
   * Called when the demonstration has finished, and awaited before play begins.
   *
   * The renderer decides WHEN the child has seen enough; the shell decides how to ask them to go
   * on. That seam is why the prompt can be a real DOM dialog — proper Vazirmatn, proper RTL, the
   * design tokens — instead of something hand-drawn on a canvas in the corner it happened to fit.
   */
  /**
   * Awaited after the demonstration. Resolving with "replay" runs it again.
   *
   * A demonstration that plays once at a speed somebody else chose is lost for good to a child who
   * blinked, and their alternative is guessing through a game whose rule they never saw.
   */
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

interface Attempt {
  found: number[];
  wrongTaps: number;
}

export async function runDifferenceTask(opts: RunOptions): Promise<DifferenceTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h / 2, Math.min(w, h) * 0.55).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(stimulus: DifferenceStimulus): {
    attempt: Promise<Attempt>;
    simulateTap: (cell: number) => void;
    relayout: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);
    const panels = new Container();
    const pips = new Graphics();
    layer.addChild(panels, pips);

    /** A card behind each panel, so the two read as two pictures rather than one tall grid. */
    const frames = new Graphics();
    panels.addChild(frames);

    const found = new Set<number>();
    let wrongTaps = 0;
    let accepting = true;
    let resolveAttempt: ((a: Attempt) => void) | null = null;
    const budget = wrongTapBudget(stimulus);

    /** Both panels' graphics for a cell, so a found difference can be ringed in each. */
    let cellGraphics: { top: Graphics; bottom: Graphics }[] = [];
    let cellSize = 0;

    function stateOf(cell: number): "idle" | "found" {
      return found.has(cell) ? "found" : "idle";
    }

    function redraw(cell: number): void {
      const pair = cellGraphics[cell];
      if (!pair) return;
      drawToken(pair.top, stimulus.left[cell] ?? null, cellSize, stateOf(cell), accent);
      drawToken(pair.bottom, stimulus.right[cell] ?? null, cellSize, stateOf(cell), accent);
    }

    /** How many are still to find, as pips. A numeral would be text (v2 §15). */
    function drawPips(): void {
      const w = app.renderer.width;
      const total = stimulus.differences.length;
      const y = app.renderer.height - 34;
      const step = 22;
      let x = (w - (total - 1) * step) / 2;
      pips.clear();
      for (let i = 0; i < total; i++) {
        const done = i < found.size;
        pips.circle(x, y, done ? 8 : 6.5).fill({
          color: done ? COLOR.correct : COLOR.card,
          alpha: done ? 1 : 0.24,
        });
        x += step;
      }
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const { cols, rows } = stimulus;

      const gap = Math.max(6, w * 0.02);
      const panelGap = Math.max(18, h * 0.04);
      /** Frame padding, as a fraction of a cell. Part of the budget, not decoration added after. */
      const PAD = 0.22;

      /**
       * The height budget, solved rather than approximated.
       *
       * The first version subtracted the gap between panels from the space available and then
       * added it back when positioning them, so the content was one gap taller than the box it
       * was centred in — `topY` came out negative and the upper panel rode off the top. The frame
       * padding was not in the budget at all, so the borders overlapped each other and the edges.
       *
       * Everything the layout draws is in here now. Two panels, each `rows` cells plus its inner
       * gaps plus padding above and below, and one gap between them:
       *
       *   2·(rows·cell + (rows−1)·gap + 2·PAD·cell) + panelGap = usable
       *
       * solved for `cell`. The width constraint is the same shape, and the smaller wins.
       */
      const topReserved = 64; // the back control sits over the canvas
      const bottomReserved = 56; // the pip row
      const usableH = h - topReserved - bottomReserved;

      const byHeight =
        (usableH - 2 * (rows - 1) * gap - panelGap) / (2 * rows + 4 * PAD);
      const byWidth = (w * 0.92 - (cols - 1) * gap) / (cols + 2 * PAD);
      cellSize = Math.max(24, Math.min(byHeight, byWidth));

      const framePad = cellSize * PAD;
      const panelW = cellSize * cols + gap * (cols - 1);
      const panelH = cellSize * rows + gap * (rows - 1);
      const frameH = panelH + framePad * 2;
      const contentH = frameH * 2 + panelGap;

      const originX = (w - panelW) / 2 + cellSize / 2;
      const topFrameY = topReserved + (usableH - contentH) / 2;
      const topY = topFrameY + framePad + cellSize / 2;
      const bottomY = topY + frameH + panelGap;

      panels.removeChildren().forEach((c) => {
        if (c !== frames) c.destroy();
      });
      panels.addChild(frames);
      cellGraphics = [];

      // The frames. Identical on purpose: two panels drawn the same way say "compare us" in a way
      // that two bare grids never did — without them a child sees one 3×4 board, and comparing
      // halves of a single board is not an idea that occurs to anyone.
      frames.clear();
      for (const cellTop of [topY, bottomY]) {
        frames
          .roundRect(
            originX - cellSize / 2 - framePad,
            cellTop - cellSize / 2 - framePad,
            panelW + framePad * 2,
            panelH + framePad * 2,
            cellSize * 0.3,
          )
          .fill({ color: 0xffffff, alpha: 0.05 })
          .stroke({ width: 2, color: accent, alpha: 0.35 });
      }

      for (let cell = 0; cell < cols * rows; cell++) {
        const col = cell % cols;
        const row = Math.floor(cell / cols);
        const x = originX + col * (cellSize + gap);

        const pair = { top: new Graphics(), bottom: new Graphics() };
        pair.top.position.set(x, topY + row * (cellSize + gap));
        pair.bottom.position.set(x, bottomY + row * (cellSize + gap));

        for (const g of [pair.top, pair.bottom]) {
          g.eventMode = "static";
          g.cursor = "pointer";
          const half = cellSize / 2 + cellSize * 0.1;
          g.hitArea = {
            contains: (hx: number, hy: number) =>
              hx >= -half && hy >= -half && hx <= half && hy <= half,
          };
          g.on("pointertap", () => onTap(cell, g));
          panels.addChild(g);
        }
        cellGraphics.push(pair);
        redraw(cell);
      }
      drawPips();
    }

    function onTap(cell: number, g: Graphics): void {
      if (!accepting) return;

      if (found.has(cell)) {
        // Already found. Acknowledged rather than ignored — an unanswered tap reads as broken and
        // gets tapped again, which the contamination detectors then see as a multi-touch anomaly.
        tick();
        return;
      }

      ripple(fx, g.position.x, g.position.y, accent, cellSize);

      if (stimulus.differences.includes(cell)) {
        found.add(cell);
        cellTone(cell + 2);
        redraw(cell);
        drawPips();
        const pair = cellGraphics[cell];
        if (pair) {
          burst(fx, pair.top.position.x, pair.top.position.y, [accent, COLOR.correct], 10);
          burst(fx, pair.bottom.position.x, pair.bottom.position.y, [accent, COLOR.correct], 10);
          void pop(pair.top, { scale: 1.16 });
          void pop(pair.bottom, { scale: 1.16 });
        }
        if (found.size === stimulus.differences.length) {
          accepting = false;
          successChime();
          void idle(520).then(() => resolveAttempt?.({ found: [...found], wrongTaps }));
        }
        return;
      }

      wrongTaps++;
      missTone();
      void nudge(g, 5);
      if (wrongTaps > budget) {
        accepting = false;
        // Reveal what was left, so the trial teaches rather than only scoring.
        void (async () => {
          for (const cell2 of stimulus.differences) {
            if (found.has(cell2)) continue;
            const pair = cellGraphics[cell2];
            if (!pair) continue;
            drawToken(pair.top, stimulus.left[cell2] ?? null, cellSize, "found", accent);
            drawToken(pair.bottom, stimulus.right[cell2] ?? null, cellSize, "found", accent);
            glow(glowLayer, pair.top.position.x, pair.top.position.y, cellSize * 0.6, COLOR.correct, 500);
            cellTone(cell2 + 2, { volume: 0.09 });
            await idle(320);
          }
          await idle(420);
          resolveAttempt?.({ found: [...found], wrongTaps });
        })();
      }
    }

    layout();
    if (!prefersReducedMotion()) {
      panels.children.forEach((child, i) => {
        child.scale.set(0);
        void idle(60 + i * 24).then(() => animate(260, (t) => child.scale.set(easeOutBack(t))));
      });
    }

    return {
      attempt: new Promise<Attempt>((resolve) => {
        resolveAttempt = resolve;
      }),
      /** Lets the tutorial play a move itself. The rule is shown, never stated. */
      simulateTap: (cell: number) => {
        const pair = cellGraphics[cell];
        if (pair) onTap(cell, pair.top);
      },
      relayout: layout,
      cleanup: () => {
        accepting = false;
        layer.destroy({ children: true });
      },
    };
  }

  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: DifferenceTaskResult["reason"] = "completed";
  let onResize: (() => void) | null = null;

  /**
   * A wordless demonstration, before anything is scored.
   *
   * Two grids of pictures do not announce that they are a pair to be compared, and there is no way
   * to say so — nothing on this surface may be text (v2 §15). So the game plays one move itself:
   * the panels appear, a differing cell rings in both at once, a pip fills. That is the entire
   * rule, shown in about two seconds.
   *
   * It runs on its own arrangement under a `demo` form id, never on a scored trial. Using trial
   * zero would hand the child the answer to the trial they are about to play.
   *
   * Not recorded. Every child sees exactly the same demonstration, so it carries no information
   * about any of them — if it ever becomes conditional on something other than "first time", that
   * stops being true and it needs to go in the record.
   */
  async function playTutorial(): Promise<void> {
    // Looped: the child can watch it again as many times as they want.
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateStimulus(task, DEMO_FORM, 0, 1);
      const cell = demo.differences[0];
      if (cell === undefined) return;

      const shown = present(demo);
      app.renderer.on("resize", shown.relayout);
      // A beat to look at the two panels before anything moves.
      await idle(950);
      if (!opts.signal?.aborted) {
        shown.simulateTap(cell);
        await untilAbandoned(shown.attempt, opts.signal);
        // Held until the child taps, rather than advancing on a timer. A demonstration that clears
        // itself before it has been looked at has demonstrated nothing, and the pace should belong
        // to the slowest child rather than to a delay I picked.
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      app.renderer.off("resize", shown.relayout);
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();
    for (;;) {
      const stimulus = opts.generateStimulus(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The reference panel, and the changed cells as the answer — enough to re-score the trial
        // from the record alone, without the client build that produced it.
        stimulus: stimulus.left,
        options: [stimulus.right],
        correctIndex: 0,
      });

      const start = performance.now();
      const trial = present(stimulus);
      onResize = trial.relayout;
      app.renderer.on("resize", trial.relayout);

      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      app.renderer.off("resize", trial.relayout);
      onResize = null;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const correct = opts.scoreTrial(stimulus, outcome.found, outcome.wrongTaps);
      if (correct) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        response: outcome.found.sort((a, b) => a - b),
        correct,
        responseMs: elapsed,
        // The search, not just the result: how many cells were tried on the way separates a child
        // who scanned from one who guessed, and an accuracy score contains none of it.
        corrections: outcome.wrongTaps,
      });

      const fast = detectImplausibleSpeed(elapsed, stimulus.differences.length);
      if (fast) await recorder.record("input_anomaly", fast);
      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, correct);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    if (onResize) app.renderer.off("resize", onResize);
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
